import { api } from '@/lib/api';
import { useEffect } from 'react';

const STORAGE_KEY = 'lyrraone_ref';

/** Capture ?ref=CODE depuis l'URL, enregistre le clic et pose le cookie httpOnly. */
export async function captureAffiliateRefFromUrl(): Promise<void> {
  if (typeof window === 'undefined') return;
  const ref = new URLSearchParams(window.location.search).get('ref')?.trim();
  if (!ref) return;
  sessionStorage.setItem(STORAGE_KEY, ref.toUpperCase());
  try {
    await api.post(`/public/lyrraone/ref/${encodeURIComponent(ref)}/track`);
  } catch {
    /* tracking non bloquant */
  }
}

export function getStoredAffiliateRef(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return sessionStorage.getItem(STORAGE_KEY) ?? undefined;
}

/** Capture ?ref= dès le montage de n'importe quelle page publique. */
export function useAffiliateRefCapture(): void {
  useEffect(() => {
    void captureAffiliateRefFromUrl();
  }, []);
}

export function buildAffiliateFormationShareUrl(
  siteSlug: string,
  formationId: string,
  affiliateCode: string,
): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/s/${encodeURIComponent(siteSlug)}/formations/${encodeURIComponent(formationId)}?ref=${encodeURIComponent(affiliateCode)}`;
}

/** Lien de tracking serveur : enregistre le clic, pose le cookie, redirige vers la cible. */
export function buildAffiliateTrackUrl(relativePath: string, affiliateCode: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const pathOnly = relativePath.split('?')[0];
  const normalized = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  return `${base}/api/public/lyrraone/ref/${encodeURIComponent(affiliateCode)}?redirect=${encodeURIComponent(normalized)}`;
}

/** Conserve ?ref= dans les returnUrl après login. */
export function withAffiliateReturnQuery(path: string): string {
  if (typeof window === 'undefined') return path;
  const ref = new URLSearchParams(window.location.search).get('ref')?.trim()
    ?? getStoredAffiliateRef();
  if (!ref) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}ref=${encodeURIComponent(ref)}`;
}

export function buildAffiliateFunnelUrl(funnelSlug: string, affiliateCode: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/f/${encodeURIComponent(funnelSlug)}?ref=${encodeURIComponent(affiliateCode)}`;
}

/** Réécrit un lien affilié absolu vers l'origine courante (port local, domaine custom). */
export function resolveAffiliateLinkUrl(absoluteUrl: string): string {
  if (typeof window === 'undefined') return absoluteUrl;
  try {
    const parsed = new URL(absoluteUrl);
    const current = new URL(window.location.href);
    parsed.protocol = current.protocol;
    parsed.host = current.host;
    return parsed.toString();
  } catch {
    return absoluteUrl;
  }
}

export function buildAffiliatePublicPageUrl(siteSlug: string, customDomainSlug?: string | null): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  if (customDomainSlug === siteSlug) return `${base}/affiliate`;
  return `${base}/s/${encodeURIComponent(siteSlug)}/affiliate`;
}
